import {
  ArcGISIdentityManager,
  IOAuth2Options,
} from '@esri/arcgis-rest-request';
import React, { createContext, useEffect, useState } from 'react';
import useLocalAccountState from '../hooks/useLocalAccountState';

const AccountManagerContext = createContext<AccountManager | 'NOT_SET'>(
  'NOT_SET'
);

const localOptionsKey = 'arcgis_auth_options';

export type AccountManager = {
  accountManager: ArcGISIdentityManager | undefined;
  login: (newOpts?: Partial<IOAuth2Options>) => void;
  logout: () => void;
};
export const AccountManagerProvider = ({
  defaultOptions,
  children,
}: {
  children: React.ReactNode;
  defaultOptions: IOAuth2Options;
}) => {
  const [options, setOptions] = useState(defaultOptions);
  const [accountManager, setAccountManager] = useLocalAccountState();

  const login = (newOpts?: Partial<IOAuth2Options>) => {
    setOptions((prevOptions) => {
      const combinedOpts = { ...prevOptions, ...newOpts };
      localStorage.setItem(localOptionsKey, JSON.stringify(combinedOpts));
      const promise = ArcGISIdentityManager.beginOAuth2(combinedOpts);
      if (promise) {
        promise.then((v) => {
          if (v.canRefresh) v.refreshCredentials();
          setAccountManager(v);
        });
      } else {
        setAccountManager(undefined);
      }
      return combinedOpts;
    });
  };

  // Logout by revoking token access
  const logout = () => {
    if (!accountManager) return;

    accountManager.signOut();
    setAccountManager(undefined);
  };

  useEffect(() => {
    if (accountManager) {
      if (!accountManager.canRefresh) {
        login();
        return;
      }

      if (accountManager.canRefresh) {
        const timeout = setInterval(
          async () => {
            const accountManagerCopy = new ArcGISIdentityManager({
              refreshToken: accountManager.refreshToken,
              refreshTokenExpires: accountManager.refreshTokenExpires,
              token: accountManager.token,
              tokenExpires: accountManager.tokenExpires,
              tokenDuration: accountManager.tokenDuration,
              redirectUri: accountManager.redirectUri,
              clientId: accountManager.clientId,
              portal: accountManager.portal,
              username: accountManager.username,
              password: accountManager.password,
            });

            const newAccountManager =
              await accountManagerCopy.exchangeRefreshToken();

            setAccountManager(newAccountManager);
          },
          1000 * 60 * 15
        );
        return () => clearInterval(timeout);
      }
    }
  }, [accountManager, setAccountManager]);
  return (
    <AccountManagerContext.Provider value={{ accountManager, login, logout }}>
      {children}
    </AccountManagerContext.Provider>
  );
};

export const useAccountManager = () => {
  const context = React.useContext(AccountManagerContext);

  if (context === 'NOT_SET') {
    throw new Error(
      'useTableState must be used within a TableStateProvider. Wrap a parent component in <TableStateProvider> to fix this error.'
    );
  }
  return context;
};
